import React, { useCallback, useEffect, useState } from "react";
import Briciole from "@/components/Briciole";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, RefreshCw, Download, ShieldCheck, ExternalLink,
         DatabaseBackup, AlertTriangle, CheckCircle2, Loader2, HardDrive, ImageDown,
         Activity } from "lucide-react";

const pesoLeggibile = (byte) => {
  if (!byte) return "—";
  const mb = byte / 1048576;
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
};

/**
 * Le foto caricate prima che l'app le rimpicciolisse da sola.
 *
 * Prima si guarda: quante sono, quanto pesano, quanto peserebbero. Poi, se
 * conviene, si fa. Tocca file veri, quindi il conto si vede prima e il
 * pulsante lo preme una persona, non un programma di notte.
 */
function RipassoFotoCard() {
  const [conto, setConto] = useState(null);
  const [guardo, setGuardo] = useState(false);
  const [lavoro, setLavoro] = useState(false);
  const [fatto, setFatto] = useState(null);

  const guarda = async () => {
    setGuardo(true);
    try {
      const { data } = await api.get("/admin/foto/ripasso");
      setConto(data);
      if (!data.da_ridurre) toast.success("Le foto sono già tutte a posto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel conteggio"); }
    finally { setGuardo(false); }
  };

  const esegui = async () => {
    if (!window.confirm(
      `Rimpicciolire ${conto.da_ridurre} foto, liberando circa ${pesoLeggibile(conto.liberati)}?\n\n` +
      "Le foto restano dove sono e con lo stesso nome, ma il file viene riscritto " +
      "più leggero: l'originale a piena risoluzione non si recupera più.\n\n" +
      "Fai prima un salvataggio, con il pulsante qui sotto.")) return;
    setLavoro(true);
    try {
      const { data } = await api.post("/admin/foto/ripasso");
      setFatto(data);
      setConto(null);
      toast.success(`${data.ridotte} foto rimpicciolite · liberati ${pesoLeggibile(data.liberati)}`);
      if (data.errori?.length) toast.warning(`${data.errori.length} non riuscite`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel ripasso"); }
    finally { setLavoro(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="ripasso-foto">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <ImageDown className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 className="font-display font-bold">Foto già caricate</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Le foto nuove vengono rimpicciolite da sole. Quelle caricate prima
                sono ancora a piena risoluzione: qui si ripassano. I documenti
                scansionati non si toccano.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0 flex-wrap">
            <Button variant="outline" onClick={guarda} disabled={guardo || lavoro}
                    data-testid="foto-guarda">
              {guardo ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Conto...</>
                      : <>Guarda cosa cambierebbe</>}
            </Button>
            {conto?.da_ridurre > 0 && (
              <Button onClick={esegui} disabled={lavoro}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="foto-esegui">
                {lavoro ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> In corso...</>
                        : <>Rimpicciolisci</>}
              </Button>
            )}
          </div>
        </div>

        {conto && (
          <div className="text-sm border border-slate-200 rounded p-3 bg-slate-50">
            {conto.da_ridurre === 0 ? (
              <span>Le {conto.foto} foto sono già alla misura giusta: non c&apos;è niente da guadagnare.</span>
            ) : (
              <>
                <div>
                  <span className="font-semibold">{conto.da_ridurre}</span> foto su {conto.foto} si possono
                  rimpicciolire: da {pesoLeggibile(conto.prima)} a {pesoLeggibile(conto.dopo)},
                  {" "}<span className="font-semibold text-emerald-700">
                    {pesoLeggibile(conto.liberati)} liberati ({conto.percentuale}%)
                  </span>.
                </div>
                {conto.piu_pesanti?.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500">
                    Le più pesanti: {conto.piu_pesanti.slice(0, 3).map(
                      (f) => `${f.file} ${pesoLeggibile(f.prima)}→${pesoLeggibile(f.dopo)}`).join(" · ")}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {fatto && (
          <div className="text-sm border border-emerald-300 bg-emerald-50 rounded p-3">
            Fatto: {fatto.ridotte} foto rimpicciolite, {fatto.lasciate} lasciate come erano,
            {" "}{pesoLeggibile(fatto.liberati)} liberati.
            {fatto.errori?.length > 0 && (
              <div className="text-xs text-red-700 mt-1">
                Non riuscite: {fatto.errori.join(" · ")}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Lo spazio rimasto sul disco dei file caricati.
 *
 * Quando finisce, l'app smette di salvare e l'errore salta fuori all'ultimo
 * passo - un POS che non esce, una foto che non si carica - senza che nessuno
 * avesse modo di vederlo arrivare. Qui si vede prima, e si vede chi lo occupa.
 */
function SpazioCard() {
  const [dati, setDati] = useState(null);
  useEffect(() => {
    api.get("/admin/spazio").then(({ data }) => setDati(data)).catch(() => setDati(null));
  }, []);
  if (!dati) return null;
  const stretto = dati.poco_spazio;
  const percento = dati.percentuale_usata;
  return (
    <Card className={stretto ? "border-red-300 bg-red-50" : "border-slate-200"} data-testid="spazio-disco">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <HardDrive className={`w-5 h-5 flex-shrink-0 mt-0.5 ${stretto ? "text-red-600" : "text-slate-500"}`} />
          <div className="min-w-0 flex-1">
            <h2 className="font-display font-bold">Spazio per i file caricati</h2>
            <p className={`text-sm mt-0.5 ${stretto ? "text-red-800 font-medium" : "text-slate-600"}`}>
              {pesoLeggibile(dati.libero)} liberi su {pesoLeggibile(dati.totale)}
              {percento != null && ` · occupato il ${percento}%`}
              {" · "}{dati.file} file caricati per {pesoLeggibile(dati.caricati)}
            </p>
            {stretto && (
              <p className="text-sm text-red-800 mt-1">
                Lo spazio sta finendo: i prossimi caricamenti falliscono con
                «No space left on device». Va ingrandito il volume del servizio
                backend, oppure vanno tolti file che non servono più.
              </p>
            )}
          </div>
        </div>
        <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
          <div className={stretto ? "h-full bg-red-500" : "h-full bg-[#0067b2]"}
               style={{ width: `${Math.min(100, percento || 0)}%` }} />
        </div>
        <div className="grid gap-1 sm:grid-cols-2">
          {(dati.cartelle || []).slice(0, 8).map((c) => (
            <div key={c.nome} className="flex items-center justify-between gap-2 text-xs
                                         border border-slate-200 bg-white rounded px-2 py-1">
              <span className="truncate">{c.nome}</span>
              <span className="text-slate-500 flex-shrink-0 tabular-nums">
                {pesoLeggibile(c.byte)} · {c.file} file
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** Stato dei salvataggi notturni: un backup che fallisce in silenzio non protegge nulla. */
function BackupCard() {
  const [stato, setStato] = useState(null);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(() => {
    api.get("/admin/backup/status").then(({ data }) => setStato(data)).catch(() => setStato(null));
  }, []);
  useEffect(() => { carica(); }, [carica]);

  /**
   * La prova del salvataggio: lo riapre e conta cosa c'e' dentro.
   *
   * Un salvataggio che nessuno ha mai riaperto non e' un salvataggio, e'
   * un file. Serve soprattutto dopo aver cambiato le chiavi dello spazio
   * remoto: se le chiavi nuove sono sbagliate, si scopre qui e non la
   * notte in cui serve ripristinare.
   */
  const [prova, setProva] = useState(null);
  const [provando, setProvando] = useState(false);
  const provaRipristino = async () => {
    setProvando(true);
    try {
      const { data } = await api.get("/admin/backup/verifica");
      setProva(data);
      if (!data.configurato || !data.trovato) toast.error(data.messaggio || "Niente da provare");
      else if (data.collezioni_mancanti?.length) toast.error("Nel salvataggio manca qualcosa");
      else toast.success("Il salvataggio si riapre ed è completo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante la prova");
    } finally { setProvando(false); }
  };

  /**
   * La diagnosi: manda roba di taglie diverse e cronometra.
   *
   * Nasce dopo tre tentativi di indovinare perche' il caricamento cadesse
   * (prima i pezzi in parallelo, poi la loro dimensione, poi il pezzo
   * unico). Misurare costa un minuto e vale piu' di tre ipotesi.
   */
  const [diagnosi, setDiagnosi] = useState(null);
  const [diagnosticando, setDiagnosticando] = useState(false);
  const provaIlCollegamento = async () => {
    setDiagnosticando(true);
    setDiagnosi(null);
    try {
      const { data } = await api.post("/admin/backup/diagnosi");
      setDiagnosi(data);
      if (!data.configurato) toast.error(data.messaggio);
      else toast.info(data.conclusione);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante la diagnosi");
    } finally { setDiagnosticando(false); }
  };

  /**
   * La copia del database scaricata a mano.
   *
   * Non chiede niente a Backblaze: e' la strada che resta quando verso
   * Backblaze non si riesce a scrivere niente, nemmeno 64 kB, mentre verso
   * altri siti si scrive benissimo. Il file arriva nel browser e tocca a
   * chi lo scarica metterlo al sicuro.
   */
  const [scaricando, setScaricando] = useState(false);
  const scaricaIlDatabase = async () => {
    setScaricando(true);
    try {
      const { data } = await api.get("/admin/backup/scarica-database",
                                     { responseType: "blob" });
      const oggi = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(new Blob([data], { type: "application/gzip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `sgm-backup-${oggi}-db.tar.gz`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Copia scaricata: mettila sul disco aziendale");
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Copia non riuscita");
    } finally { setScaricando(false); }
  };

  /**
   * Fare spazio: due tempi, perche' cancella salvataggi veri.
   *
   * Il primo giro non tocca niente e mostra l'elenco; solo dopo compare il
   * pulsante rosso che cancella davvero. Su dati di produzione una
   * cancellazione si guarda prima di farla.
   */
  const [spazio, setSpazio] = useState(null);
  const [spazioInCorso, setSpazioInCorso] = useState(false);
  const guardaLoSpazio = async (esegui) => {
    setSpazioInCorso(true);
    try {
      const { data } = await api.post(`/admin/backup/fai-spazio?esegui=${esegui}`);
      setSpazio(data);
      if (!data.configurato) toast.error(data.messaggio);
      else if (esegui) {
        toast.success(`Liberati ${data.libera_mb} MB`);
        carica();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSpazioInCorso(false); }
  };

  const eseguiOra = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      if (data.ok) toast.success(`Salvataggio completato · ${pesoLeggibile(data.size_bytes)}`);
      else toast.error(data.error || "Salvataggio fallito");
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante il salvataggio"); }
    finally { setBusy(false); }
  };

  if (!stato) return null;
  const ultimo = stato.last;
  const vecchio = ultimo?.started_at &&
    (Date.now() - new Date(ultimo.started_at).getTime()) > 36 * 3600 * 1000;
  // Quello che conta davvero: da quanti giorni non ne va a buon fine uno.
  // L'ultimo tentativo puo' essere di stanotte ed essere fallito, come e'
  // successo ogni notte fra il 21 settembre e il 23 ottobre 2026.
  const giorniSenza = stato.giorni_da_ultimo_riuscito;
  // Se il motore non ha ancora questo dato - frontend pubblicato prima del
  // backend, succede: i due servizi si pubblicano separati - meglio tacere
  // che annunciare che non esiste nessuna copia quando invece non lo sappiamo.
  const sappiamo = stato.ultimo_riuscito !== undefined;
  const senzaCopiaRecente = stato.configured && sappiamo &&
    (!stato.ultimo_riuscito || (giorniSenza !== null && giorniSenza > 1));
  // Una copia scaricata a mano e' una copia vera, ma non e' il salvataggio
  // automatico: finche' lo spazio remoto non riceve niente il riquadro
  // resta giallo, altrimenti un problema aperto sparisce dalla vista.
  const soloAMano = !!stato.ultimo_riuscito?.scaricata_a_mano;

  return (
    <Card className={stato.configured && ultimo?.ok && !vecchio && !senzaCopiaRecente && !soloAMano
      ? "border-slate-200" : "border-amber-300 bg-amber-50"}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <DatabaseBackup className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 className="font-display font-bold">Salvataggio dati e documenti</h2>
              {!stato.configured ? (
                <p className="text-sm text-amber-800 mt-1">
                  Non ancora attivo: mancano le variabili <span className="font-mono text-xs">BACKUP_S3_*</span> sul
                  servizio backend. Database e file caricati non vengono salvati da nessuna parte.
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Dati ogni notte alle {String(stato.hour).padStart(2, "0")}:00 ({stato.timezone}),
                    conservati {stato.retention_days} giorni
                    {stato.giorno_allegati && (
                      <> · allegati di {stato.giorno_allegati}, conservati{" "}
                        {Math.round(stato.giorni_allegati / 7)} settimane</>
                    )}
                  </p>
                  {ultimo ? (
                    <div className="flex items-center gap-2 mt-2 text-sm flex-wrap">
                      {ultimo.ok
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        : <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                      <span className={ultimo.ok ? "text-slate-700" : "text-red-700 font-medium"}>
                        Ultimo: {new Date(ultimo.started_at).toLocaleString("it-IT")}
                      </span>
                      {ultimo.ok && <Badge variant="outline" className="text-[10px]">{pesoLeggibile(ultimo.size_bytes)}</Badge>}
                      {!ultimo.ok && <span className="text-xs text-red-700">{ultimo.error}</span>}
                      {vecchio && ultimo.ok && (
                        <Badge className="bg-amber-500 text-white border-0 text-[10px]">più di 36 ore fa</Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-amber-800 mt-1">Nessun salvataggio ancora eseguito.</p>
                  )}
                  {senzaCopiaRecente && (
                    <p className="text-sm text-red-700 font-medium mt-1">
                      {stato.ultimo_riuscito
                        ? <>L&apos;ultima copia davvero riuscita è del{" "}
                           {new Date(stato.ultimo_riuscito.started_at).toLocaleDateString("it-IT")}
                           {", "}{giorniSenza} giorni fa.</>
                        : <>Nessun salvataggio è mai andato a buon fine.</>}
                      {" "}Premi &laquo;Scarica il database&raquo; e metti il file al sicuro.
                    </p>
                  )}
                  {soloAMano && !senzaCopiaRecente && (
                    <p className="text-sm text-amber-800 mt-1">
                      L&apos;ultima copia è stata scaricata a mano il{" "}
                      {new Date(stato.ultimo_riuscito.started_at).toLocaleDateString("it-IT")}:
                      {" "}controlla che quel file sia sul disco aziendale. Lo spazio remoto
                      non riceve ancora niente.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0 flex-wrap">
            <Button variant="outline" onClick={provaRipristino}
                    disabled={provando || !stato.configured}
                    data-testid="backup-verifica-btn">
              {provando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Controllo...</>
                        : <><ShieldCheck className="w-4 h-4 mr-2" /> Prova il salvataggio</>}
            </Button>
            <Button onClick={scaricaIlDatabase} disabled={scaricando}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="backup-scarica-btn">
              {scaricando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparo...</>
                          : <><Download className="w-4 h-4 mr-2" /> Scarica il database</>}
            </Button>
            <Button variant="outline" onClick={() => guardaLoSpazio(false)}
                    disabled={spazioInCorso || !stato.configured}
                    data-testid="backup-spazio-btn">
              {spazioInCorso ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Conto...</>
                             : <><HardDrive className="w-4 h-4 mr-2" /> Fai spazio</>}
            </Button>
            <Button variant="outline" onClick={provaIlCollegamento}
                    disabled={diagnosticando || !stato.configured}
                    data-testid="backup-diagnosi-btn">
              {diagnosticando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Misuro...</>
                              : <><Activity className="w-4 h-4 mr-2" /> Misura il collegamento</>}
            </Button>
            <Button variant="outline" onClick={eseguiOra} disabled={busy || !stato.configured}
                    data-testid="backup-run-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> In corso...</>
                    : <><DatabaseBackup className="w-4 h-4 mr-2" /> Esegui adesso</>}
            </Button>
          </div>
        </div>
        {spazio?.da_togliere && (
          <div className="mt-3 border-t border-slate-200 pt-3">
            {spazio.eseguito ? (
              <p className="text-sm font-medium text-emerald-700">
                Liberati {spazio.libera_mb} MB. Nello spazio remoto restano{" "}
                {spazio.da_tenere.length} archivi per {spazio.resta_mb} MB.
              </p>
            ) : (
              <>
                <p className="text-sm font-medium">
                  Si possono liberare <span className="text-orange-600">{spazio.libera_mb} MB</span>:{" "}
                  {spazio.da_togliere.length} archivi oltre i giorni di conservazione
                  {spazio.pezzi_interrotti.quanti > 0 && (
                    <> e {spazio.pezzi_interrotti.quanti} caricamenti interrotti
                       ({spazio.pezzi_interrotti.peso_mb} MB)</>
                  )}.
                </p>
                <p className="text-xs text-slate-600 mt-2">
                  Restano {spazio.da_tenere.length} archivi per {spazio.resta_mb} MB, fra cui:
                </p>
                <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
                  {spazio.da_tenere.slice(0, 4).map((a) => (
                    <li key={a.nome} className="font-mono">
                      {a.nome} · {new Date(a.quando).toLocaleDateString("it-IT")} ·{" "}
                      <span className="font-sans">{a.perche}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => guardaLoSpazio(true)} disabled={spazioInCorso}
                        className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="backup-spazio-esegui-btn">
                  Cancella davvero i {spazio.da_togliere.length} archivi
                </Button>
                <p className="text-[11px] text-slate-500 mt-2">
                  Non si torna indietro. Le tre copie più recenti non vengono toccate.
                </p>
              </>
            )}
          </div>
        )}
        {diagnosi?.passi && (
          <div className="mt-3 border-t border-slate-200 pt-3">
            <p className="text-sm font-medium">{diagnosi.conclusione}</p>
            <div className="mt-2 overflow-x-auto">
              <table className="text-xs min-w-[420px]">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left pr-4 font-medium">Prova</th>
                    <th className="text-left pr-4 font-medium">Esito</th>
                    <th className="text-right pr-4 font-medium">Secondi</th>
                    <th className="text-right font-medium">MB al secondo</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnosi.passi.map((p, i) => (
                    <tr key={i} className={p.ok ? "" : "text-red-700"}>
                      <td className="pr-4">{p.prova}</td>
                      <td className="pr-4">{p.nota || (p.ok ? "passata" : "non passata")}</td>
                      <td className="text-right pr-4 tabular-nums">{p.secondi}</td>
                      <td className="text-right tabular-nums">{p.mb_al_secondo ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {diagnosi.passi.filter(p => !p.ok && p.errore).map((p, i) => (
              <p key={i} className="text-[11px] text-red-700 mt-2 break-all">
                <span className="font-medium">{p.prova}</span>: {p.errore}
              </p>
            ))}
            {diagnosi.file_caricati && (
              <p className="text-[11px] text-slate-500 mt-2">
                I file caricati sono {diagnosi.file_caricati.quanti} e pesano{" "}
                {diagnosi.file_caricati.peso_mb} MB. Le prove sono state cancellate
                dallo spazio remoto.
              </p>
            )}
          </div>
        )}
        {prova?.trovato && (
          <div className="mt-3 border-t border-slate-200 pt-3">
            <p className="text-sm">
              <strong>{prova.esito}</strong>
              <span className="text-slate-500">
                {" · "}salvataggio del {new Date(prova.quando).toLocaleString("it-IT")}
                {" · "}{prova.peso_mb} MB · {prova.file_caricati_nel_salvataggio} file caricati
              </span>
            </p>
            <div className="mt-2 overflow-x-auto">
              <table className="text-xs min-w-[380px]">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left pr-4 font-medium">Elenco</th>
                    <th className="text-right pr-4 font-medium">Adesso</th>
                    <th className="text-right font-medium">Nel salvataggio</th>
                  </tr>
                </thead>
                <tbody>
                  {prova.collezioni.filter(r => r.adesso > 0).map(r => (
                    <tr key={r.collezione}
                        className={prova.collezioni_mancanti.includes(r.collezione)
                          ? "text-red-700 font-medium" : ""}>
                      <td className="pr-4">{r.collezione}</td>
                      <td className="text-right pr-4 tabular-nums">{r.adesso}</td>
                      <td className="text-right tabular-nums">{r.nel_salvataggio ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Uno scarto fra le due colonne è normale: il salvataggio è di stanotte e
              durante il giorno l&apos;archivio cresce. Conta quando una riga è rossa.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const METHOD_COLOR = {
  POST: "bg-emerald-100 text-emerald-700",
  PUT: "bg-blue-100 text-blue-700",
  PATCH: "bg-blue-100 text-blue-700",
  DELETE: "bg-red-100 text-red-700",
};

const describe = (m, p) => {
  const rules = [
    [/\/deliveries$/, "Registrazione consegna DPI"],
    [/\/certificates\/.+\/archive/, "Archiviazione documento"],
    [/\/certificates\/.+\/restore/, "Ripristino documento"],
    [/\/certificates\/.+$/, m === "DELETE" ? "Eliminazione documento" : "Modifica documento"],
    [/\/certificates$/, "Caricamento documento"],
    [/\/employees\/bulk-import-documents/, "Import massivo documenti"],
    [/\/employees\/.+\/photo/, "Caricamento foto dipendente"],
    [/\/employees\/.+\/restore/, "Ripristino dipendente"],
    [/\/employees\/.+$/, m === "DELETE" ? "Archiviazione dipendente" : "Modifica dipendente"],
    [/\/employees$/, "Creazione dipendente"],
    [/\/equipment\/bulk-import-documents/, "Import massivo documenti mezzi"],
    [/\/equipment\/maintenance/, "Registrazione manutenzione"],
    [/\/equipment\/.+\/maintenance\/import/, "Import manutenzioni"],
    [/\/equipment\/.+\/maintenance/, "Registrazione manutenzione"],
    [/\/equipment\/documents\/.+\/archive/, "Archiviazione documento mezzo"],
    [/\/equipment\/documents\/.+/, m === "DELETE" ? "Eliminazione documento mezzo" : "Modifica documento mezzo"],
    [/\/equipment\/.+\/documents/, "Caricamento documento mezzo"],
    [/\/equipment\/.+\/photo/, "Caricamento foto mezzo"],
    [/\/equipment\/.+\/restore/, "Ripristino mezzo"],
    [/\/equipment\/.+\/permanent/, "Eliminazione definitiva mezzo"],
    [/\/equipment\/.+$/, m === "DELETE" ? "Archiviazione mezzo" : "Modifica dati mezzo"],
    [/\/equipment$/, "Creazione mezzo"],
    [/\/items\/.+\/adjust/, "Carico/scarico magazzino"],
    [/\/items/, "Gestione articolo DPI"],
    [/\/auth\/login/, "Accesso al sistema"],
    [/\/auth\/logout/, "Uscita dal sistema"],
  ];
  for (const [re, label] of rules) if (re.test(p)) return label;
  return `${m} ${p}`;
};

// Aree per filtrare il registro: "chi ha toccato cosa" sui mezzi, sui
// dipendenti, sui cantieri e via dicendo.
const AREE = [
  { key: "tutte", label: "Tutte" },
  { key: "mezzi", label: "Mezzi e attrezzature" },
  { key: "dipendenti", label: "Dipendenti" },
  { key: "cantieri", label: "Cantieri" },
  { key: "magazzino", label: "Magazzino" },
  { key: "azienda", label: "Documenti aziendali" },
  { key: "sistema", label: "Sistema" },
];

export default function RegistroAttivita() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("tutte");
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: "300" });
      if (onlyErrors) params.append("only_errors", "true");
      const { data } = await api.get(`/audit-log?${params.toString()}`);
      setRows(data.items || []);
    } catch { toast.error("Errore caricamento registro"); }
    finally { setLoading(false); }
  }, [onlyErrors]);

  useEffect(() => { load(); }, [load]);

  const term = q.trim().toLowerCase();
  const soggetti = (r) => (r.subjects || []).map(s => `${s.type}: ${s.label}`).join(" — ");
  const filtered = rows.filter(r =>
    (area === "tutte" || r.area === area) &&
    (!term ||
      (r.user_email || "").toLowerCase().includes(term) ||
      (r.path || "").toLowerCase().includes(term) ||
      soggetti(r).toLowerCase().includes(term) ||
      describe(r.method, r.path).toLowerCase().includes(term)));

  const conteggi = Object.fromEntries(
    AREE.map(a => [a.key, a.key === "tutte" ? rows.length : rows.filter(r => r.area === a.key).length]));

  const exportCsv = () => {
    const head = "data_ora;utente;area;operazione;soggetto;metodo;percorso;esito\n";
    const body = filtered.map(r =>
      [new Date(r.at).toLocaleString("it-IT"), r.user_email, r.area, describe(r.method, r.path),
       soggetti(r), r.method, r.path, r.ok ? "ok" : `errore ${r.status}`]
        .map(v => String(v ?? "").replace(/;/g, ",")).join(";")
    ).join("\n");
    const blob = new Blob(["\uFEFF" + head + body], { type: "text/csv;charset=utf-8;" });
    const u = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `registro_attivita_${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(u);
  };

  return (
    <div className="space-y-6" data-testid="registro-page">
      <Briciole voci={[
        { testo: "Dashboard", a: "/" },
        { testo: "Registro attività" },
      ]} />

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Admin</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Registro attività</h1>
          <p className="text-sm text-slate-500 mt-1">
            Tracciabilità delle operazioni: chi ha fatto cosa e quando
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-11" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
          </Button>
          <Button variant="outline" className="h-11" onClick={exportCsv}>
            <Download className="w-4 h-4 mr-2" /> Esporta CSV
          </Button>
        </div>
      </div>

      <SpazioCard />
      <RipassoFotoCard />
      <BackupCard />

      <div className="flex gap-2 flex-wrap">
        {AREE.map(a => (
          <button key={a.key} type="button" onClick={() => setArea(a.key)}
                  data-testid={`audit-area-${a.key}`}
                  className={`px-3 py-2 rounded-full text-xs font-medium border transition whitespace-nowrap ${
                    area === a.key
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}>
            {a.label} ({conteggi[a.key] ?? 0})
          </button>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca per utente, operazione, targa o documento..." className="pl-9 h-11"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 px-2">
          <input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)} />
          Solo operazioni fallite
        </label>
      </div>

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!loading && filtered.length === 0 && (
        <Card className="border-dashed border-2">
          <CardContent className="p-10 text-center">
            <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold text-slate-700">Nessuna attività registrata</h3>
            <p className="text-sm text-slate-500 mt-1">
              Le operazioni compiute nell'applicazione compariranno qui.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[920px]">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-3">Data e ora</th>
                  <th className="text-left p-3">Utente</th>
                  <th className="text-left p-3">Operazione</th>
                  <th className="text-left p-3">Su cosa</th>
                  <th className="text-left p-3">Metodo</th>
                  <th className="text-left p-3">Esito</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${!r.ok ? "bg-red-50/50" : ""}`}>
                    <td className="p-3 whitespace-nowrap text-slate-600">
                      {new Date(r.at).toLocaleString("it-IT")}
                    </td>
                    <td className="p-3 truncate max-w-[180px]">{r.user_email || "—"}</td>
                    <td className="p-3">
                      <div>{describe(r.method, r.path)}</div>
                      <div className="text-[10px] text-slate-400 font-mono truncate">{r.path}</div>
                    </td>
                    <td className="p-3 max-w-[280px]">
                      {(r.subjects || []).length === 0 ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <div className="space-y-0.5">
                          {r.subjects.map((s, k) => (
                            <div key={k} className="leading-tight">
                              <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.type}</div>
                              {s.link ? (
                                <Link to={s.link} className="text-orange-600 hover:underline inline-flex items-center gap-1">
                                  <span className="truncate">{s.label}</span>
                                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                </Link>
                              ) : (
                                <span className="truncate block">{s.label}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge className={`${METHOD_COLOR[r.method] || "bg-slate-100 text-slate-700"} border-0 text-[10px]`}>
                        {r.method}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {r.ok
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">ok</Badge>
                        : <Badge variant="destructive" className="text-[10px]">errore {r.status}</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
